/**
 * 🛵 MAX DELIVERY ASSISTANT - Assistant Livraison Intelligent
 * 
 * Max guide le client de A à Z pour passer sa commande de livraison
 * avec un style pop-up immersif comme Pixie !
 * 
 * PERSONNALISÉ avec le prénom du client !
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Send, MapPin, Clock, Package, Truck, ChevronRight, 
  CheckCircle, Loader2, Phone, MessageCircle, Sparkles,
  Navigation, Calculator, AlertCircle, Star, Zap, User
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

// Avatar de Max le livreur
const MAX_AVATAR = 'https://static.prod-images.emergentagent.com/jobs/90b8cfd5-4d07-402e-9a12-a0e9be89c0f2/images/d2dd79574adadc4218e7bf647409093e50bc748604d8f603eea8192b674d4867.png';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Fonction pour normaliser les noms de villes (enlever accents, tirets, etc.)
const normalizeCity = (str) => {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Enlever les accents
    .replace(/[-']/g, ' ') // Remplacer tirets et apostrophes par espaces
    .replace(/\s+/g, ' ') // Normaliser les espaces
    .trim();
};

// Fonction pour obtenir le salut selon l'heure
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour >= 6 && hour < 18) return 'Bonjour';
  return 'Bonsoir';
};

// Zones de livraison avec toutes les villes desservies - TOUT EN VERT = ON LIVRE ! 💚
const DELIVERY_ZONES = {
  zone_proche: {
    name: 'Zone Express',
    emoji: '💚',
    minOrder: 20,
    fee: 0,
    time: '15-25 min',
    cities: [
      'Vermelles', 'Noyelles-lès-Vermelles', 'Noyelles les Vermelles', 
      'Annequin', 'Mazingarbe', 'Hulluch'
    ],
    color: 'from-green-500 to-emerald-500'
  },
  zone_moyenne: {
    name: 'Zone Confort', 
    emoji: '💚',
    minOrder: 30,
    fee: 0,
    time: '25-40 min',
    cities: [
      'Beuvry', 'Labourse', 'Sailly-Labourse', 'Cambrin', 
      'Cuinchy', 'Douvrin', 'Violaines', 'Billy-Berclau',
      'Auchy-les-Mines', 'Auchy les Mines', 'Festubert',
      'Givenchy-lès-la-Bassée', 'Givenchy les la Bassee',
      'Grenay', 'Bully-les-Mines', 'Bully les Mines'
    ],
    color: 'from-green-500 to-emerald-500'
  },
  zone_eloignee: {
    name: 'Zone Étendue',
    emoji: '💚',
    minOrder: 40,
    fee: 0,
    time: '35-50 min',
    cities: [
      'Béthune', 'Bethune', 'Lens', 'Liévin', 'Lievin',
      'Noeux-les-Mines', 'Noeux les Mines', 'Nœux-les-Mines',
      'Wingles', 'Harnes', 'Loos-en-Gohelle', 'Loos en Gohelle',
      'Sallaumines', 'Vendin-le-Vieil', 'Vendin le Vieil',
      'Sains-en-Gohelle', 'Sains en Gohelle',
      'Autres'
    ],
    color: 'from-green-500 to-emerald-500'
  }
};

// Liste des villes pour les questions fréquentes
const ALL_CITIES = [
  'Vermelles', 'Noyelles-lès-Vermelles', 'Annequin', 'Mazingarbe', 'Hulluch',
  'Beuvry', 'Labourse', 'Sailly-Labourse', 'Cambrin', 'Cuinchy', 'Douvrin',
  'Violaines', 'Billy-Berclau', 'Auchy-les-Mines', 'Festubert', 'Givenchy-lès-la-Bassée',
  'Grenay', 'Bully-les-Mines', 'Béthune', 'Lens', 'Liévin', 'Nœux-les-Mines',
  'Wingles', 'Harnes', 'Loos-en-Gohelle', 'Sallaumines', 'Vendin-le-Vieil', 'Sains-en-Gohelle'
];

// Messages de Max selon les étapes - PERSONNALISÉS !
const MAX_MESSAGES = {
  welcome: (greeting) => `${greeting} ! 👋 Moi c'est Max de La Petite Épicerie, et je vais vous aider à passer votre commande !\n\nTout d'abord, j'aurais besoin de votre nom et prénom s'il vous plaît :)`,
  askAddress: (firstName) => `Enchanté ${firstName} ! 😊\n\nMaintenant, inscrivez s'il vous plaît votre adresse très précisément (numéro, rue, ville).\n\nAinsi le livreur sera plus efficace pour vous livrer !`,
  zoneFound: (firstName) => `Super ${firstName} ! 💚 Nous pouvons vous livrer rapidement !`,
  askItems: (firstName) => `Parfait ${firstName} ! 🛒\n\nInscrivez le nom des produits que vous souhaitez vous faire livrer, et faites une estimation du montant à peu près.\n\nNous vous rappelons pour vous donner le prix exact !\n\n💡 **FORMULES WEEK-END disponibles !**\n☕ Sans alcool : Pack Soirée Soft, Cinéma Maison, Goûter Gourmand\n🍷 Avec alcool (18+) : Pack Apéro, Vin & Fromage, Soirée Premium`,
  askPhone: (firstName) => `Merci ${firstName} ! 📱\n\nMaintenant, votre numéro de téléphone est très important pour que notre livreur puisse vous contacter !`,
  askSlot: (firstName) => `Super ${firstName} ! ⏰\n\nNous livrons le vendredi et samedi soir de 21h à 2h du matin.\n\nChoisissez votre créneau préféré :`,
  confirmOrder: (firstName) => `Récap' de votre commande ${firstName} ! 📋\n\nVérifiez que tout est correct :`,
  success: (firstName) => `Merci pour votre commande ${firstName} ! 🎉\n\nNous faisons au mieux pour qu'elle vous arrive très vite !\n\nQuant à moi, je vous dis à très bientôt ${firstName} pour une nouvelle commande.\n\nTrès bonne soirée ${firstName} avec La Petite Épicerie ! 🛵💨`,
  error: "Oups ! 😅 Il y a eu un souci. Réessayez ou appelez-nous au 07 80 64 89 76 !",
  outOfZone: (firstName) => `Ah mince ${firstName} ! 😕 Votre ville n'est pas encore dans nos zones de livraison.\n\nMais appelez-nous au 07 80 64 89 76, on peut peut-être s'arranger ! 📞`,
  chat: "Une question ? Je suis là pour vous aider ! 💬"
};

// Questions rapides suggérées
const QUICK_ACTIONS = [
  { text: "🏠 Vérifier ma zone", action: 'checkZone' },
  { text: "📋 Nos formules", action: 'formules' },
  { text: "⏰ Horaires de livraison", action: 'hours' },
  { text: "💰 Minimum de commande", action: 'minimum' },
  { text: "📞 Appeler le magasin", action: 'call' }
];

/**
 * 🛵 MaxDeliveryAssistant - Assistant de livraison popup style Pixie
 */
export default function MaxDeliveryAssistant({ 
  isOpen, 
  onClose, 
  customer,
  onOrderSubmit
}) {
  const [step, setStep] = useState('welcome'); // welcome, name, address, items, phone, slot, confirm, success, chat
  const [isTyping, setIsTyping] = useState(false);
  const [currentMessage, setCurrentMessage] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [showChat, setShowChat] = useState(false);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  
  // Données de commande
  const [orderData, setOrderData] = useState({
    firstName: '',
    lastName: '',
    city: '',
    address: '',
    zone: null,
    items: '',
    amount: '',
    phone: '',
    slot: '',
    notes: '',
    customerName: customer?.name || '',
    customerPhone: customer?.phone || ''
  });

  // Ref pour annuler l'animation de frappe
  const typingAbortRef = useRef(false);

  // Effet de frappe pour les messages de Max (plus rapide)
  const typeMessage = async (message, delay = 15) => {
    typingAbortRef.current = true; // Annuler toute animation précédente
    await new Promise(r => setTimeout(r, 30)); // Petit délai pour laisser l'autre s'arrêter
    typingAbortRef.current = false;
    
    setIsTyping(true);
    setCurrentMessage('');
    
    // Afficher le message par chunks de 3 caractères pour aller plus vite
    const chunkSize = 3;
    for (let i = 0; i < message.length; i += chunkSize) {
      if (typingAbortRef.current) {
        // Si annulé, afficher tout le message immédiatement
        setCurrentMessage(message);
        break;
      }
      await new Promise(r => setTimeout(r, delay));
      if (typingAbortRef.current) {
        setCurrentMessage(message);
        break;
      }
      setCurrentMessage(message.substring(0, Math.min(i + chunkSize, message.length)));
    }
    setIsTyping(false);
  };
  
  // Forcer l'affichage complet du message
  const skipTyping = () => {
    typingAbortRef.current = true;
  };

  // Initialisation
  useEffect(() => {
    if (isOpen) {
      setStep('welcome');
      const greeting = getGreeting();
      typeMessage(MAX_MESSAGES.welcome(greeting));
      // Pré-remplir si on a les infos du client
      if (customer) {
        setOrderData(prev => ({
          ...prev,
          firstName: customer.firstName || customer.name?.split(' ')[0] || '',
          lastName: customer.lastName || customer.name?.split(' ').slice(1).join(' ') || '',
          customerName: customer.name || '',
          customerPhone: customer.phone || '',
          phone: customer.phone || ''
        }));
      }
    }
  }, [isOpen, customer]);

  // Scroll vers le bas des messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, currentMessage]);

  // Détecter la zone depuis la ville (avec normalisation pour accents/fautes)
  const detectZone = (city) => {
    const normalizedInput = normalizeCity(city);
    
    for (const [zoneId, zone] of Object.entries(DELIVERY_ZONES)) {
      const found = zone.cities.some(c => {
        const normalizedCity = normalizeCity(c);
        // Vérifier si l'input contient la ville ou vice versa
        return normalizedInput.includes(normalizedCity) || 
               normalizedCity.includes(normalizedInput) ||
               normalizedInput === normalizedCity;
      });
      if (found) {
        return { id: zoneId, ...zone };
      }
    }
    return null;
  };

  // Obtenir les créneaux disponibles EN TEMPS RÉEL
  const getAvailableSlots = () => {
    const now = new Date();
    const day = now.getDay(); // 0 = dimanche, 5 = vendredi, 6 = samedi
    const hour = now.getHours();
    const minutes = now.getMinutes();
    
    // Créneaux de 21h à 2h du matin
    const allSlots = [
      { label: '21:00 - 21:30', startHour: 21, startMin: 0 },
      { label: '21:30 - 22:00', startHour: 21, startMin: 30 },
      { label: '22:00 - 22:30', startHour: 22, startMin: 0 },
      { label: '22:30 - 23:00', startHour: 22, startMin: 30 },
      { label: '23:00 - 23:30', startHour: 23, startMin: 0 },
      { label: '23:30 - 00:00', startHour: 23, startMin: 30 },
      { label: '00:00 - 00:30', startHour: 0, startMin: 0 },
      { label: '00:30 - 01:00', startHour: 0, startMin: 30 },
      { label: '01:00 - 01:30', startHour: 1, startMin: 0 },
      { label: '01:30 - 02:00', startHour: 1, startMin: 30 }
    ];
    
    // Vérifier si c'est vendredi (5) ou samedi (6)
    const isDeliveryDay = day === 5 || day === 6;
    
    if (!isDeliveryDay) {
      // Si pas vendredi/samedi, afficher tous les créneaux pour la prochaine session
      return allSlots.map(s => s.label);
    }
    
    // Si vendredi/samedi, filtrer les créneaux passés
    return allSlots.filter(slot => {
      // Convertir l'heure actuelle en minutes depuis 21h
      let currentMinutes;
      if (hour >= 21) {
        currentMinutes = (hour - 21) * 60 + minutes;
      } else if (hour < 3) {
        currentMinutes = (hour + 3) * 60 + minutes; // Après minuit
      } else {
        return true; // Pendant la journée, tous les créneaux sont dispo
      }
      
      // Convertir le créneau en minutes depuis 21h
      let slotMinutes;
      if (slot.startHour >= 21) {
        slotMinutes = (slot.startHour - 21) * 60 + slot.startMin;
      } else {
        slotMinutes = (slot.startHour + 3) * 60 + slot.startMin;
      }
      
      // Garder le créneau s'il est dans le futur (+30 min de marge)
      return slotMinutes > currentMinutes + 30;
    }).map(s => s.label);
  };

  // Gérer la progression des étapes
  const handleNext = async () => {
    skipTyping(); // Skip l'animation en cours
    
    switch (step) {
      case 'welcome':
        setStep('name');
        // Le formulaire nom/prénom s'affiche
        break;
        
      case 'name':
        if (!orderData.firstName.trim()) {
          toast.error('Votre prénom est requis !');
          return;
        }
        setStep('address');
        typeMessage(MAX_MESSAGES.askAddress(orderData.firstName));
        setTimeout(() => inputRef.current?.focus(), 300);
        break;
        
      case 'address':
        if (!orderData.city.trim() || !orderData.address.trim()) {
          toast.error('Entrez votre adresse complète !');
          return;
        }
        const zone = detectZone(orderData.city);
        if (zone) {
          setOrderData(prev => ({ ...prev, zone }));
          setStep('items');
          typeMessage(MAX_MESSAGES.zoneFound(orderData.firstName) + '\n\n' + MAX_MESSAGES.askItems(orderData.firstName));
        } else {
          typeMessage(MAX_MESSAGES.outOfZone(orderData.firstName));
        }
        break;
        
      case 'items':
        if (!orderData.items.trim()) {
          toast.error('Dites-nous ce que vous voulez commander !');
          return;
        }
        if (!orderData.amount || parseFloat(orderData.amount) < (orderData.zone?.minOrder || 20)) {
          toast.error(`Minimum ${orderData.zone?.minOrder || 20}€ pour la livraison !`);
          return;
        }
        setStep('phone');
        typeMessage(MAX_MESSAGES.askPhone(orderData.firstName));
        break;
        
      case 'phone':
        if (!orderData.phone.trim() || orderData.phone.length < 10) {
          toast.error('Numéro de téléphone invalide !');
          return;
        }
        setStep('slot');
        typeMessage(MAX_MESSAGES.askSlot(orderData.firstName));
        break;
        
      case 'slot':
        if (!orderData.slot) {
          toast.error('Choisissez un créneau !');
          return;
        }
        setStep('confirm');
        typeMessage(MAX_MESSAGES.confirmOrder(orderData.firstName));
        break;
        
      case 'confirm':
        await submitOrder();
        break;
    }
  };

  // Soumettre la commande
  const submitOrder = async () => {
    setIsTyping(true);
    try {
      const response = await fetch(`${API_URL}/api/delivery/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: customer?.id,
          customerName: `${orderData.firstName} ${orderData.lastName}`.trim(),
          customerPhone: orderData.phone,
          customerAddress: `${orderData.address}, ${orderData.city}`,
          zone: orderData.zone?.id,
          items: orderData.items,
          totalAmount: parseFloat(orderData.amount),
          preferredSlot: orderData.slot,
          notes: orderData.notes,
          source: 'max_assistant'
        })
      });
      
      if (response.ok) {
        setStep('success');
        typeMessage(MAX_MESSAGES.success(orderData.firstName));
        toast.success('🎉 Commande envoyée !');
        onOrderSubmit?.();
      } else {
        throw new Error('Erreur API');
      }
    } catch (err) {
      console.error('Erreur:', err);
      typeMessage(MAX_MESSAGES.error);
      toast.error("Erreur lors de l'envoi");
    } finally {
      setIsTyping(false);
    }
  };

  // Gérer le chat - INTELLIGENT avec détection des villes !
  const handleChatMessage = async () => {
    if (!userInput.trim()) return;
    
    const userMsg = userInput.trim();
    setChatMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setUserInput('');
    setIsTyping(true);
    
    const lowerMsg = userMsg.toLowerCase();
    let response = "";
    
    // 1. Détecter si une ville est mentionnée dans la question
    const mentionedCity = ALL_CITIES.find(city => 
      normalizeCity(lowerMsg).includes(normalizeCity(city))
    );
    
    if (mentionedCity) {
      // Vérifier si on livre dans cette ville
      const zone = detectZone(mentionedCity);
      if (zone) {
        response = `✅ Oui, nous livrons à ${mentionedCity} ! 🛵\n\nC'est dans notre ${zone.name} (${zone.emoji}).\n\n• Minimum de commande : ${zone.minOrder}€\n• Temps de livraison : ${zone.time}\n• Livraison GRATUITE !\n\nVoulez-vous commander ?`;
      } else {
        response = `😕 Malheureusement, ${mentionedCity} n'est pas encore dans nos zones de livraison.\n\nMais appelez-nous au 07 80 64 89 76, on peut peut-être s'arranger ! 📞`;
      }
    }
    // 2. Questions sur les zones/villes en général
    else if (lowerMsg.includes('zone') || lowerMsg.includes('ville') || lowerMsg.includes('livre') || lowerMsg.includes('livraison') || lowerMsg.includes('livrez')) {
      response = `Nous livrons dans 28 villes ! 🗺️\n\n💚 Zone Express (20€ min) :\nVermelles, Noyelles, Annequin, Mazingarbe, Hulluch\n\n💚 Zone Confort (30€ min) :\nBeuvry, Labourse, Cambrin, Cuinchy, Douvrin, Violaines, Billy-Berclau, Grenay, Bully-les-Mines...\n\n💚 Zone Étendue (40€ min) :\nBéthune, Lens, Liévin, Harnes, Wingles, Loos-en-Gohelle, Sallaumines...\n\nDites-moi votre ville et je vous confirme ! 😊`;
    }
    // 3. Questions sur les horaires
    else if (lowerMsg.includes('heure') || lowerMsg.includes('horaire') || lowerMsg.includes('quand') || lowerMsg.includes('ouvert')) {
      response = "🌙 Nous livrons le vendredi et samedi soir de 21h à 2h du matin !\n\nParfait pour vos soirées !";
    }
    // 4. Questions sur le minimum
    else if (lowerMsg.includes('minimum') || lowerMsg.includes('combien') || lowerMsg.includes('prix') || lowerMsg.includes('tarif')) {
      response = "💰 Le minimum de commande est de :\n\n• 20€ pour la Zone Express (Vermelles et environs)\n• 30€ pour la Zone Confort\n• 40€ pour la Zone Étendue\n\nEt la livraison est GRATUITE ! 🆓";
    }
    // 5. Questions sur le paiement
    else if (lowerMsg.includes('paye') || lowerMsg.includes('paiement') || lowerMsg.includes('espèce') || lowerMsg.includes('carte') || lowerMsg.includes('cb')) {
      response = "💳 Nous acceptons :\n\n• Les espèces à la livraison 💵\n• La carte bancaire (CB) 💳\n\nVous choisissez au moment de la livraison !";
    }
    // 6. Questions sur les formules
    else if (lowerMsg.includes('formule') || lowerMsg.includes('pack') || lowerMsg.includes('promo') || lowerMsg.includes('offre') || lowerMsg.includes('week-end') || lowerMsg.includes('weekend')) {
      response = "🎉 **FORMULES WEEK-END** (Vendredi & Samedi)\n\n" +
        "☕ **SANS ALCOOL :**\n" +
        "• 🥤 Pack Soirée Soft - Chips, bonbons, sodas & limonades\n" +
        "• 🍿 Pack Cinéma Maison - Pop-corn, glaces, boissons fraîches\n" +
        "• 🧁 Pack Goûter Gourmand - Gâteaux, biscuits, jus de fruits\n\n" +
        "🍷 **AVEC ALCOOL (18+) :**\n" +
        "• 🍺 Pack Apéro - Bières artisanales, chips, cacahuètes\n" +
        "• 🍷 Pack Vin & Fromage - Bouteille de vin, assortiment fromages\n" +
        "• 🍾 Pack Soirée Premium - Champagne, verrines, petits fours\n\n" +
        "⚠️ Pièce d'identité demandée pour les formules avec alcool.\n" +
        "L'abus d'alcool est dangereux pour la santé.";
    }
    // 7. Remerciements
    else if (lowerMsg.includes('merci') || lowerMsg.includes('super') || lowerMsg.includes('génial') || lowerMsg.includes('cool')) {
      response = `De rien ${orderData.firstName || ''} ! C'est un plaisir de vous aider ! 😊🛵`;
    }
    // 8. Salutations
    else if (lowerMsg.includes('bonjour') || lowerMsg.includes('bonsoir') || lowerMsg.includes('salut') || lowerMsg.includes('hello')) {
      const greeting = getGreeting();
      response = `${greeting} ! 👋 Comment puis-je vous aider ?\n\n• Vérifier si on livre chez vous\n• Vous aider à passer commande\n• Répondre à vos questions`;
    }
    // 9. Réponse par défaut plus intelligente
    else {
      response = `Je ne suis pas sûr de comprendre votre question. 🤔\n\nJe peux vous aider avec :\n• Les zones de livraison 🗺️\n• Les horaires (21h-2h ven/sam) ⏰\n• Le minimum de commande 💰\n• Les moyens de paiement 💳\n\nOu appelez le 07 80 64 89 76 ! 📞`;
    }
    
    await new Promise(r => setTimeout(r, 800));
    setChatMessages(prev => [...prev, { role: 'bot', content: response }]);
    setIsTyping(false);
  };

  // Actions rapides
  const handleQuickAction = async (action) => {
    switch (action) {
      case 'checkZone':
        setStep('address');
        typeMessage("Entrez votre ville et je vous dis tout de suite si on peut vous livrer ! 🏠");
        break;
      case 'hours':
        setChatMessages(prev => [...prev, { role: 'bot', content: "🌙 Nous livrons le vendredi et samedi de 21h à 2h du matin ! Parfait pour vos soirées !" }]);
        setShowChat(true);
        break;
      case 'minimum':
        setChatMessages(prev => [...prev, { role: 'bot', content: "💰 Commande minimum : 20€ (zone express autour de Vermelles), 30€ (zone confort), 40€ (zone étendue).\n\nLivraison GRATUITE ! 🆓" }]);
        setShowChat(true);
        break;
      case 'formules':
        setChatMessages(prev => [...prev, { role: 'bot', content: "🎉 **FORMULES WEEK-END** (Vendredi & Samedi)\n\n" +
          "☕ **SANS ALCOOL :**\n" +
          "• 🥤 Pack Soirée Soft - Chips, bonbons, sodas & limonades\n" +
          "• 🍿 Pack Cinéma Maison - Pop-corn, glaces, boissons fraîches\n" +
          "• 🧁 Pack Goûter Gourmand - Gâteaux, biscuits, jus de fruits\n\n" +
          "🍷 **AVEC ALCOOL (18+) :**\n" +
          "• 🍺 Pack Apéro - Bières artisanales, chips, cacahuètes\n" +
          "• 🍷 Pack Vin & Fromage - Bouteille de vin, assortiment fromages\n" +
          "• 🍾 Pack Soirée Premium - Champagne, verrines, petits fours\n\n" +
          "⚠️ Pièce d'identité demandée pour l'alcool. À consommer avec modération." }]);
        setShowChat(true);
        break;
      case 'call':
        window.location.href = 'tel:0780648976';
        break;
    }
  };

  if (!isOpen) return null;

  const availableSlots = getAvailableSlots();

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-center justify-center p-4"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(255,140,0,0.15) 0%, rgba(10,0,20,0.98) 70%)'
        }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0, y: 50 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 50 }}
          transition={{ type: 'spring', damping: 25 }}
          className="w-full max-w-lg max-h-[90vh] overflow-x-hidden overflow-y-auto rounded-3xl relative"
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'linear-gradient(180deg, #1a0a2e 0%, #0d0015 100%)',
            border: '2px solid rgba(255, 165, 0, 0.4)',
            boxShadow: '0 0 60px rgba(255, 140, 0, 0.3), 0 0 120px rgba(255, 100, 0, 0.1)'
          }}
        >
          {/* Bouton fermer */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/30 hover:bg-black/50 transition-colors"
          >
            <X className="w-6 h-6 text-gray-400" />
          </button>

          {/* Header avec Max */}
          <div className="relative pt-8 pb-4 px-6 text-center">
            {/* Avatar de Max avec effets */}
            <motion.div
              className="relative inline-block mb-4"
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
              {/* Glow effect */}
              <div 
                className="absolute inset-0 rounded-full blur-3xl opacity-60"
                style={{ 
                  background: 'radial-gradient(circle, rgba(255, 165, 0, 0.6) 0%, rgba(255, 100, 0, 0.3) 50%, transparent 70%)',
                  transform: 'scale(1.8)'
                }}
              />
              
              {/* Avatar */}
              <motion.div
                className="relative w-28 h-28 md:w-36 md:h-36 rounded-full overflow-hidden mx-auto"
                style={{
                  border: '4px solid rgba(255, 165, 0, 0.7)',
                  boxShadow: '0 0 40px rgba(255, 165, 0, 0.5), 0 0 80px rgba(255, 100, 0, 0.3)',
                }}
                animate={{
                  boxShadow: [
                    '0 0 40px rgba(255, 165, 0, 0.5), 0 0 80px rgba(255, 100, 0, 0.3)',
                    '0 0 60px rgba(255, 165, 0, 0.7), 0 0 100px rgba(255, 100, 0, 0.5)',
                    '0 0 40px rgba(255, 165, 0, 0.5), 0 0 80px rgba(255, 100, 0, 0.3)',
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <img 
                  src={MAX_AVATAR} 
                  alt="Max le livreur" 
                  className="w-full h-full object-cover"
                />
              </motion.div>

              {/* Sparkles */}
              <motion.div
                className="absolute -top-2 -right-2"
                animate={{ scale: [1, 1.4, 1], rotate: [0, 180, 360] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <span className="text-2xl">⚡</span>
              </motion.div>
              <motion.div
                className="absolute -bottom-1 -left-3"
                animate={{ scale: [1, 1.3, 1], rotate: [0, -180, -360] }}
                transition={{ duration: 2.5, repeat: Infinity, delay: 0.3 }}
              >
                <span className="text-xl">🛵</span>
              </motion.div>
              <motion.div
                className="absolute top-0 left-0"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
              >
                <span className="text-lg">✨</span>
              </motion.div>
            </motion.div>

            {/* Nom et titre */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <h2 
                className="text-2xl font-black text-white mb-1"
                style={{ textShadow: '0 0 20px rgba(255, 165, 0, 0.8)' }}
              >
                Max le Livreur
              </h2>
              <p className="text-orange-300 text-sm mb-2">Assistant Livraison Express 🛵💨</p>
            </motion.div>

            {/* Bulle de message */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-4 rounded-2xl mx-auto max-w-sm relative"
              style={{
                background: 'linear-gradient(135deg, rgba(255, 165, 0, 0.15) 0%, rgba(255, 100, 0, 0.1) 100%)',
                border: '1px solid rgba(255, 165, 0, 0.3)',
              }}
            >
              <p className="text-white text-base leading-relaxed min-h-[60px]">
                {currentMessage}
                {isTyping && <span className="animate-pulse">▌</span>}
              </p>
            </motion.div>
          </div>

          {/* Contenu selon l'étape */}
          <div className="px-6 pb-6 max-h-[45vh] overflow-y-auto">
            {/* Étape Nom/Prénom */}
            {step === 'name' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <div>
                  <label className="text-gray-400 text-sm mb-2 block flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Votre prénom *
                  </label>
                  <input
                    ref={inputRef}
                    type="text"
                    value={orderData.firstName}
                    onChange={(e) => setOrderData(prev => ({ ...prev, firstName: e.target.value }))}
                    placeholder="Ex: Marie"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-orange-500/30 text-white placeholder-gray-500 focus:border-orange-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Votre nom</label>
                  <input
                    type="text"
                    value={orderData.lastName}
                    onChange={(e) => setOrderData(prev => ({ ...prev, lastName: e.target.value }))}
                    placeholder="Ex: Dupont"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-orange-500/30 text-white placeholder-gray-500 focus:border-orange-400 focus:outline-none"
                  />
                </div>
              </motion.div>
            )}

            {/* Étape Adresse */}
            {step === 'address' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Votre ville *</label>
                  <input
                    ref={inputRef}
                    type="text"
                    value={orderData.city}
                    onChange={(e) => setOrderData(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="Ex: Vermelles, Béthune, Lens..."
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-orange-500/30 text-white placeholder-gray-500 focus:border-orange-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Adresse complète *</label>
                  <input
                    type="text"
                    value={orderData.address}
                    onChange={(e) => setOrderData(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="Ex: 123 rue de la Paix"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-orange-500/30 text-white placeholder-gray-500 focus:border-orange-400 focus:outline-none"
                  />
                </div>
                
                {/* Zones info - TOUT EN VERT ! */}
                <div className="grid grid-cols-3 gap-2 mt-4">
                  {Object.entries(DELIVERY_ZONES).map(([id, zone]) => (
                    <motion.div 
                      key={id}
                      className="p-2 rounded-lg text-center text-xs border border-green-500/50"
                      style={{ background: 'rgba(34, 197, 94, 0.15)' }}
                      animate={{ 
                        boxShadow: [
                          '0 0 5px rgba(34, 197, 94, 0.3)',
                          '0 0 15px rgba(34, 197, 94, 0.6)',
                          '0 0 5px rgba(34, 197, 94, 0.3)'
                        ]
                      }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      <span className="text-lg">{zone.emoji}</span>
                      <p className="text-green-400 font-semibold mt-1">{zone.minOrder}€ min</p>
                      <p className="text-green-300/70 text-[10px]">✓ On livre !</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Étape Items */}
            {step === 'items' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                {orderData.zone && (
                  <motion.div 
                    className="p-3 rounded-xl bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/50"
                    animate={{ 
                      boxShadow: [
                        '0 0 10px rgba(34, 197, 94, 0.3)',
                        '0 0 20px rgba(34, 197, 94, 0.5)',
                        '0 0 10px rgba(34, 197, 94, 0.3)'
                      ]
                    }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <div className="flex items-center gap-2 text-white">
                      <span className="text-xl">{orderData.zone.emoji}</span>
                      <span className="font-semibold text-green-400">{orderData.zone.name}</span>
                      <span className="ml-auto text-sm text-green-300">⏱️ {orderData.zone.time}</span>
                    </div>
                    <p className="text-green-400/80 text-xs mt-1">✓ Super ! On livre chez toi !</p>
                  </motion.div>
                )}
                
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Qu&apos;est-ce que vous voulez ? 🛒</label>
                  <textarea
                    value={orderData.items}
                    onChange={(e) => setOrderData(prev => ({ ...prev, items: e.target.value }))}
                    placeholder="Ex: 1 bouteille de Coca, chips, bonbons, café..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-orange-500/30 text-white placeholder-gray-500 focus:border-orange-400 focus:outline-none resize-none"
                  />
                </div>
                
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Montant estimé (€) 💰</label>
                  <input
                    type="number"
                    value={orderData.amount}
                    onChange={(e) => setOrderData(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder={`Minimum ${orderData.zone?.minOrder || 20}€`}
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-orange-500/30 text-white placeholder-gray-500 focus:border-orange-400 focus:outline-none"
                  />
                  {orderData.amount && parseFloat(orderData.amount) < (orderData.zone?.minOrder || 20) && (
                    <p className="text-red-400 text-xs mt-1">
                      ⚠️ Minimum {orderData.zone?.minOrder || 20}€ pour votre zone
                    </p>
                  )}
                </div>
              </motion.div>
            )}

            {/* Étape Téléphone */}
            {step === 'phone' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <div>
                  <label className="text-gray-400 text-sm mb-2 block flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Votre numéro de téléphone *
                  </label>
                  <input
                    ref={inputRef}
                    type="tel"
                    value={orderData.phone}
                    onChange={(e) => setOrderData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="Ex: 06 12 34 56 78"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-orange-500/30 text-white placeholder-gray-500 focus:border-orange-400 focus:outline-none"
                  />
                  <p className="text-gray-500 text-xs mt-1">
                    📱 Important pour que le livreur puisse vous contacter
                  </p>
                </div>
              </motion.div>
            )}

            {/* Étape Créneau */}
            {step === 'slot' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <p className="text-gray-400 text-sm">Choisissez votre créneau (Vendredi & Samedi) ⏰</p>
                <div className="grid grid-cols-2 gap-2">
                  {availableSlots.map((slot) => (
                    <button
                      key={slot}
                      onClick={() => setOrderData(prev => ({ ...prev, slot }))}
                      className={`p-3 rounded-xl text-sm font-medium transition-all ${
                        orderData.slot === slot
                          ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white scale-105'
                          : 'bg-white/10 text-gray-300 hover:bg-white/20'
                      }`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
                
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Instructions (optionnel)</label>
                  <input
                    type="text"
                    value={orderData.notes}
                    onChange={(e) => setOrderData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Code d'entrée, étage, digicode..."
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-orange-500/30 text-white placeholder-gray-500 focus:border-orange-400 focus:outline-none"
                  />
                </div>
              </motion.div>
            )}

            {/* Étape Confirmation */}
            {step === 'confirm' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-3"
              >
                <div className="p-4 rounded-xl bg-white/5 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">👤 Client</span>
                    <span className="text-white">{orderData.firstName} {orderData.lastName}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">📱 Téléphone</span>
                    <span className="text-white">{orderData.phone}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">📍 Adresse</span>
                    <span className="text-white">{orderData.address}, {orderData.city}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">🗺️ Zone</span>
                    <span className="text-white">{orderData.zone?.emoji} {orderData.zone?.name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">🛒 Commande</span>
                    <span className="text-white text-right max-w-[60%] truncate">{orderData.items}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">💰 Montant</span>
                    <span className="text-orange-400 font-bold">{orderData.amount}€</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">⏰ Créneau</span>
                    <span className="text-white">{orderData.slot}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">🚚 Livraison</span>
                    <span className="text-green-400 font-bold">GRATUITE !</span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Étape Succès */}
            {step === 'success' && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-4"
              >
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.5 }}
                  className="text-6xl mb-4"
                >
                  🎉
                </motion.div>
                <p className="text-gray-400">Un livreur vous contactera bientôt !</p>
                <a
                  href="tel:0780648976"
                  className="inline-flex items-center gap-2 mt-4 text-orange-400 hover:text-orange-300"
                >
                  <Phone className="w-4 h-4" />
                  07 80 64 89 76
                </a>
              </motion.div>
            )}

            {/* Welcome - Actions rapides */}
            {step === 'welcome' && !showChat && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="space-y-3"
              >
                <p className="text-gray-400 text-sm text-center mb-2">Actions rapides ⚡</p>
                <div className="grid grid-cols-2 gap-2">
                  {QUICK_ACTIONS.slice(0, 4).map((action) => (
                    <button
                      key={action.action}
                      onClick={() => handleQuickAction(action.action)}
                      className="p-3 rounded-xl bg-white/5 hover:bg-white/10 text-white text-sm transition-all hover:scale-105"
                    >
                      {action.text}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => handleQuickAction('call')}
                  className="w-full p-3 rounded-xl bg-green-500/20 hover:bg-green-500/30 text-green-400 text-sm transition-all flex items-center justify-center gap-2"
                >
                  <Phone className="w-4 h-4" />
                  Appeler le magasin
                </button>
              </motion.div>
            )}

            {/* Chat */}
            {showChat && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-3"
              >
                <div className="max-h-40 overflow-y-auto space-y-2">
                  {chatMessages.map((msg, i) => (
                    <div
                      key={i}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[80%] p-3 rounded-xl text-sm ${
                        msg.role === 'user'
                          ? 'bg-orange-500 text-white'
                          : 'bg-white/10 text-white'
                      }`}>
                        {msg.content}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
                
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleChatMessage()}
                    placeholder="Posez votre question..."
                    className="flex-1 px-4 py-2 rounded-xl bg-white/10 text-white placeholder-gray-500 focus:outline-none"
                  />
                  <button
                    onClick={handleChatMessage}
                    disabled={isTyping}
                    className="p-2 rounded-xl bg-orange-500 text-white"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </motion.div>
            )}
          </div>

          {/* Footer - Boutons d'action */}
          <div className="px-6 pb-6 flex gap-3">
            {step !== 'welcome' && step !== 'success' && (
              <Button
                onClick={() => {
                  const steps = ['welcome', 'name', 'address', 'items', 'phone', 'slot', 'confirm'];
                  const currentIdx = steps.indexOf(step);
                  if (currentIdx > 0) {
                    setStep(steps[currentIdx - 1]);
                  }
                }}
                variant="outline"
                className="flex-1 border-orange-500/30 text-orange-400 hover:bg-orange-500/10"
              >
                ← Retour
              </Button>
            )}
            
            {step === 'welcome' && (
              <>
                <Button
                  onClick={() => setShowChat(!showChat)}
                  variant="outline"
                  className="flex-1 border-orange-500/30 text-orange-400 hover:bg-orange-500/10"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  {showChat ? 'Actions' : 'Chat'}
                </Button>
                <Button
                  onClick={handleNext}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:from-orange-600 hover:to-amber-600"
                >
                  Commander 🛵
                  <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </>
            )}
            
            {step !== 'welcome' && step !== 'success' && (
              <Button
                onClick={handleNext}
                disabled={isTyping}
                className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:from-orange-600 hover:to-amber-600"
              >
                {isTyping ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : step === 'confirm' ? (
                  <>Envoyer la commande 🚀</>
                ) : (
                  <>Suivant <ChevronRight className="w-5 h-5 ml-1" /></>
                )}
              </Button>
            )}
            
            {step === 'success' && (
              <Button
                onClick={onClose}
                className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white"
              >
                Fermer ✨
              </Button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
